import type { SimMainResult } from "./ganttTypes";
import { CHART, niceTicks, stepDecimals, fmtT, fmtH } from "./chartUtils";
import type { UnitSystem } from "@/lib/units";
import { dispToKg, goalLabel, kgToDisp, tickSuffix } from "./ganttUnits";

/** Grey band between incoming and consumed: product waiting to be loaded. */
export const BUF_FILL = "rgba(94,110,125,.14)";
/** The band's grey, strong enough to read as a small swatch (tooltip). */
export const BUF_SWATCH = "rgba(94,110,125,.35)";

const FONT      = "11px Montserrat, Segoe UI, sans-serif";
const FONT_MARK = "600 10px Montserrat, Segoe UI, sans-serif";

/** Buffer-chart figures, shared by the chart and its card. */
export interface BufferStats {
  /** Samples usable across buffer_t_min / cum_in_kg / cum_out_kg. */
  n: number;
  /** Everything loaded (the last consumed value): incoming is capped here. */
  totalKg: number;
  /** Incoming capped at totalKg, one value per sample. */
  inKg: number[];
  /** The capped incoming line as drawn: the samples plus the exact knee. */
  lineT: number[];
  lineKg: number[];
  /** t* (min from the shift start): incoming reaches totalKg. Null when the
   *  cap never bites (incoming never exceeds totalKg + 1 kg) or t* ≤ 0. */
  supplyEndMin: number | null;
  /** Largest capped buffer (capped incoming − consumed) and when it first occurs. */
  peakKg: number;
  peakMin: number;
  /** The frozen series, or null when it is missing (it can be on Scenarios). */
  frozen: number[] | null;
  frozenMaxKg: number;
  /** When the frozen line first reaches the goal (min); null if never or no goal. */
  goalMin: number | null;
}

/**
 * Supply stops once the last batch is covered: no load ever uses product that
 * arrives after t*, so capping incoming at the total loaded is exact (and keeps
 * the line from climbing off the chart after the last load of the day).
 */
export function bufferStats(r: SimMainResult, goalT: number): BufferStats {
  const t = r.buffer_t_min || [], cin = r.cum_in_kg || [], out = r.cum_out_kg || [];
  const n = Math.min(t.length, cin.length, out.length);
  const fr = r.frozen_kg || [];
  const frozen = n > 0 && fr.length >= n ? fr : null;
  const totalKg = n > 0 ? out[n - 1] : 0;

  const inKg: number[] = [], lineT: number[] = [], lineKg: number[] = [];
  let knee: number | null = null;
  let cinMax = -Infinity;
  for (let i = 0; i < n; i++) {
    if (i > 0 && cin[i - 1] <= totalKg && cin[i] > totalKg) {
      // the exact crossing between the two samples keeps the corner sharp
      const tk = t[i - 1] + ((totalKg - cin[i - 1]) / (cin[i] - cin[i - 1])) * (t[i] - t[i - 1]);
      if (tk > t[i - 1]) { lineT.push(tk); lineKg.push(totalKg); }
      if (knee == null) knee = tk;
    }
    const v = Math.min(cin[i], totalKg);
    inKg.push(v); lineT.push(t[i]); lineKg.push(v);
    if (cin[i] > cinMax) cinMax = cin[i];
  }
  // Only mark supply ending when the cap really bites (more than float noise).
  const supplyEndMin = cinMax > totalKg + 1 && knee != null && knee > 0 ? knee : null;

  let peakKg = 0, peakMin = n > 0 ? t[0] : 0;
  for (let i = 0; i < n; i++) {
    const b = inKg[i] - out[i];
    if (b > peakKg) { peakKg = b; peakMin = t[i]; }
  }

  const goalKg = goalT > 0 ? goalT * 1000 : 0;
  const hit = goalKg * (1 - 1e-9);              // float guard: an exact hit counts
  let frozenMaxKg = 0;
  let goalMin: number | null = null;
  if (frozen) {
    for (let i = 0; i < n; i++) {
      if (frozen[i] > frozenMaxKg) frozenMaxKg = frozen[i];
      if (goalKg > 0 && goalMin == null && frozen[i] >= hit) {
        // where the drawn line crosses the goal, so the dot sits on it
        if (i === 0) goalMin = t[0];
        else {
          const f = (goalKg - frozen[i - 1]) / (frozen[i] - frozen[i - 1]);
          goalMin = t[i - 1] + Math.min(1, Math.max(0, f)) * (t[i] - t[i - 1]);
        }
      }
    }
  }

  return { n, totalKg, inKg, lineT, lineKg, supplyEndMin, peakKg, peakMin, frozen, frozenMaxKg, goalMin };
}

/** Frozen short of the goal. A near miss drops to 2 dp (rounded down) so it never
 *  prints as the goal itself: 19.97 t reads "19.97 t", not "20.0 t". */
function shortOf(kg: number, goalKg: number, sys: UnitSystem = "SI"): string {
  if (sys === "US") {
    // Same rule in US tons: the printed figure must stay below the goal.
    const ton = kgToDisp(kg, sys);
    const txt = ton.toFixed(1);
    return dispToKg(parseFloat(txt), sys) < goalKg ? `${txt} ton` : `${(Math.floor(ton * 100) / 100).toFixed(2)} ton`;
  }
  const txt = fmtT(kg);
  return parseFloat(txt) * 1000 < goalKg ? txt : `${(Math.floor(kg / 10) / 100).toFixed(2)} t`;
}

/** The geometry of a canvas's last drawing, for the hover hit-test. */
interface Geom {
  W: number; H: number;
  x0: number; x1: number; y0: number; y1: number;
  tMax: number;
  sx: (tm: number) => number;
  sy: (kg: number) => number;
  t: number[];
  out: number[];
  s: BufferStats;
}
const geoms = new WeakMap<HTMLCanvasElement, Geom>();

/**
 * Draws the product buffer chart (cumulative incoming / consumed / frozen lines)
 * onto a canvas element. Incoming is capped at the total loaded (bufferStats),
 * and each drawing records its geometry for bufferReadout.
 */
export function drawBufferChart(
  canvas: HTMLCanvasElement,
  r: SimMainResult,
  goalT: number,
  sys: UnitSystem = "SI",
) {
  const W = canvas.clientWidth;
  if (!W) return;
  const H = 240;
  const dpr = window.devicePixelRatio || 1;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  const c = canvas.getContext("2d")!;
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  c.clearRect(0, 0, W, H);

  const s = bufferStats(r, goalT);
  const t = r.buffer_t_min || [];
  const out = r.cum_out_kg || [];
  // The series run to the last sample: the shift end, or the last unload when
  // unloading overtime is used (flat after the shift — inflow stops).
  const tMax = t[t.length - 1] || 1;
  const goalKg = goalT > 0 ? goalT * 1000 : 0;
  const otH = Math.max(0, r.overtime_used_h ?? 0);
  const shiftMin = (r.t_shift_h ?? 0) * 60;
  const showShiftEnd = otH > 0 && shiftMin > 0 && shiftMin < tMax - 1e-6;

  // Round-number tonnes (US: tons) with ~6 % headroom. Capped incoming tops out
  // at the total loaded, so the goal, that total and the frozen line set the scale.
  const yt = niceTicks(0, kgToDisp(Math.max(goalKg, s.totalKg, s.frozenMaxKg) * 1.06, sys), 5);
  const yMax = dispToKg(yt.max, sys);
  const dec = stepDecimals(yt.step);
  const tickTxt = (v: number) => `${v.toFixed(dec)}${tickSuffix(sys)}`;

  c.font = FONT;
  // 42 px as always in SI; US tick labels ("22.5 ton") are wider, so it grows to fit.
  const padL = sys === "US"
    ? Math.max(42, Math.ceil(Math.max(...yt.ticks.map(v => c.measureText(tickTxt(v)).width))) + 12)
    : 42;
  const padR = 42, padT = 12, padB = 30;
  const x0 = padL, x1 = W - padR, y0 = H - padB, y1 = padT;

  const sx = (tm: number) => x0 + (tm / tMax) * (x1 - x0);
  const sy = (kg: number) => y0 - (kg / yMax) * (y0 - y1);
  geoms.set(canvas, { W, H, x0, x1, y0, y1, tMax, sx, sy, t, out, s });

  // Small round marker with a white ring, so it reads on top of a line.
  const dot = (x: number, y: number, rad: number, col: string) => {
    c.beginPath(); c.arc(x, y, rad, 0, Math.PI * 2);
    c.fillStyle = col; c.fill();
    c.lineWidth = 1.5; c.strokeStyle = "#fff"; c.stroke();
  };
  // Text over the plot: a thin white halo keeps it legible across lines and the band.
  const label = (txt: string, x: number, y: number, col: string) => {
    c.lineJoin = "round"; c.lineWidth = 3; c.strokeStyle = "rgba(255,255,255,.85)";
    c.strokeText(txt, x, y);
    c.fillStyle = col; c.fillText(txt, x, y);
  };

  c.font = FONT;

  // Grid + Y labels, every label with the same decimals
  yt.ticks.forEach(v => {
    const y = sy(dispToKg(v, sys));
    c.globalAlpha = 0.5; c.strokeStyle = CHART.grid; c.lineWidth = 1;
    c.beginPath(); c.moveTo(x0, y); c.lineTo(x1, y); c.stroke();
    c.globalAlpha = 1; c.fillStyle = CHART.axis;
    c.textAlign = "right"; c.textBaseline = "middle";
    c.fillText(tickTxt(v), x0 - 8, y);
  });

  // X labels
  c.fillStyle = CHART.axis;
  c.textAlign = "center"; c.textBaseline = "top";
  const tStep = Math.max(60, Math.ceil(tMax / 8 / 60) * 60);
  for (let tm = 0; tm <= tMax + 1e-9; tm += tStep) {
    c.fillText((tm / 60).toFixed(0) + "h", sx(tm), y0 + 8);
  }

  // Clip the series to the plot area — a safety net now that everything fits the scale.
  c.save();
  c.beginPath(); c.rect(x0, y1, x1 - x0, y0 - y1); c.clip();

  // Overtime region (shift end → last unload), lightly shaded under the series.
  if (showShiftEnd) {
    c.fillStyle = "rgba(75,75,157,.08)";
    c.fillRect(sx(shiftMin), y1, x1 - sx(shiftMin), y0 - y1);
  }

  // Buffer band: along capped incoming (with its knee), back along consumed.
  if (s.n > 1) {
    c.beginPath();
    s.lineT.forEach((tm, i) => {
      if (i) c.lineTo(sx(tm), sy(s.lineKg[i]));
      else c.moveTo(sx(tm), sy(s.lineKg[i]));
    });
    for (let i = s.n - 1; i >= 0; i--) c.lineTo(sx(t[i]), sy(out[i]));
    c.closePath();
    c.fillStyle = BUF_FILL;
    c.fill();
  }

  const drawLine = (ts: number[], vs: number[], m: number, col: string) => {
    c.strokeStyle = col; c.lineWidth = 2.2;
    c.beginPath();
    for (let i = 0; i < m; i++) {
      if (i) c.lineTo(sx(ts[i]), sy(vs[i]));
      else c.moveTo(sx(ts[i]), sy(vs[i]));
    }
    c.stroke();
  };
  drawLine(s.lineT, s.lineKg, s.lineT.length, CHART.incoming);
  drawLine(t, out, s.n, CHART.consumed);
  if (s.frozen) drawLine(t, s.frozen, s.n, CHART.frozen);

  c.restore();

  // Goal line (dashed, across the plot) — under the markers, as the knee can sit
  // just above it when the plant only just meets the goal.
  const Yg = sy(goalKg);
  if (goalKg > 0) {
    c.strokeStyle = CHART.goal; c.setLineDash([3, 3]); c.lineWidth = 1;
    c.beginPath(); c.moveTo(x0, Yg); c.lineTo(x1, Yg); c.stroke();
    c.setLineDash([]);
  }

  // Shift-end marker (dashed) with its label in the top padding, clear of the series.
  if (showShiftEnd) {
    const Xs = sx(shiftMin);
    c.strokeStyle = "#4B4B9D"; c.lineWidth = 1.2; c.setLineDash([4, 3]);
    c.beginPath(); c.moveTo(Xs, y1); c.lineTo(Xs, y0); c.stroke();
    c.setLineDash([]);
    c.font = FONT_MARK;
    c.fillStyle = "#4B4B9D"; c.textAlign = "center"; c.textBaseline = "bottom";
    c.fillText("Shift end", Xs, y1 - 1);
    c.font = FONT;
  }

  // Supply ends: a dot at the knee and a dotted drop to the axis, labelled just
  // above the axis (the "Shift end" label sits at the top, so they never collide).
  if (s.supplyEndMin != null) {
    const Xk = sx(s.supplyEndMin), Yk = sy(s.totalKg);
    c.strokeStyle = CHART.incoming; c.lineWidth = 1; c.setLineDash([2, 3]);
    c.beginPath(); c.moveTo(Xk, Yk); c.lineTo(Xk, y0); c.stroke();
    c.setLineDash([]);
    dot(Xk, Yk, 3, CHART.incoming);
    c.font = FONT_MARK;
    const txt = `Supply ends ${fmtH(s.supplyEndMin / 60)}`;
    // left of the line; to its right when it would run past the axis
    const onRight = Xk - 5 - c.measureText(txt).width < x0;
    c.textAlign = onRight ? "left" : "right"; c.textBaseline = "bottom";
    label(txt, onRight ? Xk + 5 : Xk - 5, y0 - 3, CHART.incoming);
    c.font = FONT;
  }

  // Goal outcome at the left, above the line, and a dot where the frozen line
  // first reaches it.
  if (goalKg > 0) {
    const goalTxt = sys === "US"
      ? goalLabel(goalT, sys)
      : `${goalT.toLocaleString("en-GB", { maximumFractionDigits: 2 })} t`;
    const outcome = s.goalMin != null ? ` · reached ${fmtH(s.goalMin / 60)}`
      : s.frozen ? ` · not reached (${shortOf(s.frozenMaxKg, goalKg, sys)})`
      : "";
    c.textAlign = "left"; c.textBaseline = "bottom";
    label(`Goal ${goalTxt}${outcome}`, x0 + 6, Yg - 4, CHART.goal);
    if (s.goalMin != null) dot(sx(s.goalMin), Yg, 4, CHART.goal);
  }
}

/** The hover read-out at the sample nearest the cursor. Positions in CSS px from the canvas's top-left. */
export interface BufferReadout {
  x: number;                  // crosshair
  top: number;                // plot area
  bottom: number;
  width: number;              // canvas size, to keep the tooltip inside
  height: number;
  tMin: number;               // sample time, min from the shift start
  inKg: number;               // capped incoming
  outKg: number;              // consumed
  frozenKg: number | null;
  bufKg: number;              // in the buffer: capped incoming − consumed
  yIn: number;                // dots on the lines
  yOut: number;
  yFrozen: number | null;
}

/**
 * Hit-test the last drawing of `canvas`: the read-out at the sample nearest
 * `mouseX`, or null outside the plot area (also vertically, given `mouseY`).
 * Pure — reads the geometry drawBufferChart recorded; never redraws.
 */
export function bufferReadout(
  canvas: HTMLCanvasElement,
  mouseX: number,
  mouseY?: number,
): BufferReadout | null {
  const g = geoms.get(canvas);
  if (!g || !g.s.n || mouseX < g.x0 || mouseX > g.x1) return null;
  if (mouseY != null && (mouseY < g.y1 || mouseY > g.y0)) return null;
  const { t, out, s } = g;
  const tm = ((mouseX - g.x0) / (g.x1 - g.x0)) * g.tMax;
  // nearest sample: binary search over the ascending sample times
  let lo = 0, hi = s.n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (t[mid] <= tm) lo = mid; else hi = mid;
  }
  const i = Math.abs(t[hi] - tm) < Math.abs(t[lo] - tm) ? hi : lo;
  const frozenKg = s.frozen ? s.frozen[i] : null;
  return {
    x: g.sx(t[i]), top: g.y1, bottom: g.y0, width: g.W, height: g.H,
    tMin: t[i],
    inKg: s.inKg[i],
    outKg: out[i],
    frozenKg,
    bufKg: Math.max(0, s.inKg[i] - out[i]),
    yIn: g.sy(s.inKg[i]),
    yOut: g.sy(out[i]),
    yFrozen: frozenKg == null ? null : g.sy(frozenKg),
  };
}
